/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  takeLatest,
  ForkEffect,
  put,
  call,
  cancelled,
} from 'redux-saga/effects';
import ActionConstants from '../constants/';
import {
  COMMON_ERROR_CODES,
  CleverTapEventNames,
  DOWNLOAD_FILE_MORE_THAN_EXPECTED_RECORD_ERROR_MESSAGE,
} from '../../lib/constants';
import notify from 'lib/notifiy';
import store from 'redux/store';
import { pushClevertapEvent } from 'lib/analyticsUtil';

// ------------------------------------
// Worker
// ------------------------------------
function* downloadCsvWorkerSaga(action) {
  const {
    serviceMethod,
    actionTypeRequest,
    extra,
    genericErrorHandling = true,
    hideLoader,
    isDirectUrl,
  } = action.payload;
  let loaderStarted = false;
  let loaderStopped = false;
  try {
    yield put({
      type: actionTypeRequest,
      payload: extra,
    });
    if (!hideLoader) {
      yield put({
        type: ActionConstants.START_LOADER,
        payload: { actionType: actionTypeRequest },
      });
      loaderStarted = true;
    }

    const apiResponse = yield call(serviceMethod);

    const { text, headers, body } = apiResponse;
    if (isDirectUrl) {
      const tempLink = document.createElement('a');
      tempLink.href = JSON.parse(text)?.url;
      tempLink.setAttribute(
        'download',
        extra?.fileName || headers['content-disposition']?.split('filename=')[1]
      );
      pushClevertapEvent({
        eventName: CleverTapEventNames.DOWNLOAD_SUCCESS,
      });
      tempLink.click();
      tempLink.remove();

      if (!hideLoader) {
        yield put({
          type: ActionConstants.STOP_LOADER,
          payload: { actionType: actionTypeRequest },
        });
        loaderStopped = true;
      }
    } else {
      const downloadFile = (url: string, defaultName: string): void => {
        const tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.setAttribute(
          'download',
          extra?.fileName ||
          (headers['content-disposition']?.split('filename=')[1] ??
            defaultName)
        );
        pushClevertapEvent({
          eventName: CleverTapEventNames.DOWNLOAD_SUCCESS,
        });
        tempLink.click();
        tempLink.remove();
      };

      // Downloading File
      if (text && headers) {
        const csvData = new Blob([text], { type: 'text/csv;' });
        const csvURL = window.URL.createObjectURL(csvData);
        downloadFile(csvURL, 'file.csv');
      } else if (body && headers) {
        const url = URL.createObjectURL(new Blob([apiResponse.body]));
        downloadFile(url, extra?.fileName || 'file.xlsx');
      } else {
        throw new Error('Data not found');
      }

      if (!hideLoader) {
        yield put({
          type: ActionConstants.STOP_LOADER,
          payload: { actionType: actionTypeRequest },
        });
        loaderStopped = true;
      }
    }
  } catch (error: any) {
    if (loaderStarted && !loaderStopped) {
      yield put({
        type: ActionConstants.STOP_LOADER,
        payload: { actionType: actionTypeRequest },
      });
      loaderStopped = true;
    }
    if (error && error.response && error.response.body) {
      pushClevertapEvent({
        eventName: CleverTapEventNames.DOWNLOAD_FAILED,
      });
      // Common Generic Error Handling
      if (
        COMMON_ERROR_CODES[error.response.body.error_code] &&
        genericErrorHandling
      ) {
        notify({
          type: 'error',
          message:
            store.getState().validationReducer.validationErrorState
              .validationErrors.SOMETHING_WENT_WRONG ||
            'Something went wrong! Please try again.',
          description: '',
        });
      } else if (
        error.response.body.error_message ===
        DOWNLOAD_FILE_MORE_THAN_EXPECTED_RECORD_ERROR_MESSAGE ||
        error.response.body.errorMessage ===
        DOWNLOAD_FILE_MORE_THAN_EXPECTED_RECORD_ERROR_MESSAGE
      ) {
        notify({
          type: 'error',
          message:
            store.getState().validationReducer.validationErrorState
              .validationErrors.DOWNLOAD_FILE_MORE_THAN_EXPECTED_RECORD,
          description: '',
        });
      } else {
        notify({
          type: 'error',
          message:
            error?.response?.body?.errorMessage ||
            store.getState().validationReducer.validationErrorState
              .validationErrors.SOMETHING_WENT_WRONG,
          description: '',
        });
      }
    } else {
      notify({
        description:
          store.getState().validationReducer.validationErrorState
            .validationErrors.SOMETHING_WENT_WRONG ||
          'Something went wrong! Please try again.',
        duration: 1,
        type: 'error',
        message: 'Error',
      });
      // eslint-disable-next-line no-console
      console.error(error);
    }
  } finally {
    if (loaderStarted && !loaderStopped && (yield cancelled())) {
      yield put({
        type: ActionConstants.STOP_LOADER,
        payload: { actionType: actionTypeRequest },
      });
    }
  }
}

// ------------------------------------
// Watchers
// ------------------------------------
export default function* downloadCsvWatcher(): Generator<
  ForkEffect<never>,
  void,
  unknown
> {
  yield takeLatest(
    ActionConstants.GET_DOWNLOAD_CSV_ACTION,
    downloadCsvWorkerSaga
  );
}
